import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type { ApiErrorResponse, HTTPValidationError } from '@/types/api';

export class HttpError extends Error {
  constructor(
    message: string,
    public status?: number,
    public isNetworkError = false,
    public details?: ApiErrorResponse
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

// Create axios client instance
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://loop-api.demo.lnch.dev/api/v1';

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'ngrok-skip-browser-warning': 'true',
  },
});

// Helper functions
function extractErrorMessage(errorDetails: ApiErrorResponse): string {
  if (typeof errorDetails.detail === 'string') {
    return errorDetails.detail;
  } else if (typeof errorDetails.detail === 'object') {
    if ('code' in errorDetails.detail && 'reason' in errorDetails.detail) {
      return `${errorDetails.detail.code}: ${errorDetails.detail.reason}`;
    }
    return JSON.stringify(errorDetails.detail);
  } else if ('detail' in errorDetails && Array.isArray(errorDetails.detail)) {
    const validationError = errorDetails as HTTPValidationError;
    return validationError.detail
      .map(err => `${err.loc.join('.')}: ${err.msg}`)
      .join(', ');
  }
  return 'Unknown error occurred';
}

function handleError(error: AxiosError): HttpError {
  if (error.response) {
    // Server responded with error status
    const errorDetails = error.response.data as ApiErrorResponse;
    return new HttpError(
      extractErrorMessage(errorDetails),
      error.response.status,
      false,
      errorDetails
    );
  } else if (error.request) {
    console.log(error);
    // Request made but no response
    return new HttpError('Network error: Unable to connect to API', 0, true);
  } else {
    // Error in request setup
    return new HttpError(error.message, 0, true);
  }
}

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

// Response interceptor for error handling and token refresh
client.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Don't retry auth endpoints (login, logout, refresh, register)
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/jwt/login') ||
      originalRequest.url?.includes('/auth/jwt/logout') ||
      originalRequest.url?.includes('/auth/jwt/refresh') ||
      originalRequest.url?.includes('/auth/register');

    // If we get a 401 and haven't retried yet, try to refresh the token
    // Skip refresh if explicitly marked (e.g., initial auth check)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      // If already refreshing, wait for that to complete
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          return client(originalRequest);
        } catch (refreshError) {
          return Promise.reject(handleError(error));
        }
      }

      // Start refresh process
      isRefreshing = true;
      refreshPromise = client
        .post('/auth/jwt/refresh')
        .then(() => {
          isRefreshing = false;
          refreshPromise = null;
          return client(originalRequest);
        })
        .catch(refreshError => {
          isRefreshing = false;
          refreshPromise = null;
          // Refresh failed - if it's a 401, user needs to login again
          // Don't throw a network error, just return the original 401
          if (refreshError.response?.status === 401) {
            return Promise.reject(handleError(error));
          }
          // Other refresh errors (network, etc) should be reported
          return Promise.reject(handleError(error));
        });

      return refreshPromise;
    }

    return Promise.reject(handleError(error));
  }
);

// HTTP service functions
async function get<T>(
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.get<T>(endpoint, config);
  return response.data;
}

async function post<T>(
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.post<T>(endpoint, data, config);
  return response.data;
}

async function patch<T>(
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.patch<T>(endpoint, data, config);
  return response.data;
}

async function put<T>(
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.put<T>(endpoint, data, config);
  return response.data;
}

async function deleteRequest<T>(
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.delete<T>(endpoint, config);
  return response.data;
}

// Special method for form data (like login)
async function postForm<T>(
  endpoint: string,
  data: Record<string, string>
): Promise<T> {
  const formData = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await client.post<T>(endpoint, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  return response.data;
}

export const httpService = {
  get,
  post,
  patch,
  put,
  delete: deleteRequest,
  postForm,
} as const;
